import React from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function EmployeeDrawer({ isOpen, onClose, employee }) {
  const navigate = useNavigate();

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/40 backdrop-blur-xs flex justify-end">
      <div className="w-full md:w-1/2 min-w-[420px] bg-white h-full shadow-2xl flex flex-col border-l border-slate-100 animate-in slide-in-from-right duration-300">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-sm shadow-sm shadow-blue-500/20">
              {employee.first_name?.[0]}{employee.last_name?.[0]}
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">{employee.full_name || `${employee.first_name} ${employee.last_name}`}</h3>
              <p className="text-[11px] font-medium text-slate-400">{employee.employee_code || 'N/A'}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-900 bg-slate-50 rounded-full transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="px-8 py-8 flex-1 overflow-y-auto flex flex-col gap-8 bg-white">
          
          {/* Status & Role Row */}
          <div className="flex items-center justify-between pb-6 border-b border-slate-100">
            <div>
              <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Designation</p>
              <p className="text-xs font-semibold text-slate-800 mt-1">{employee.remarks || 'Employee'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase text-right">Status</p>
              <span className={`inline-block mt-1 text-xs font-semibold px-3 py-0.5 rounded-full ${
                employee.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
              }`}>
                {employee.status || 'Active'}
              </span>
            </div>
          </div>

          {/* Contact Details Group */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">Contact Information</p>
            
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Official Email</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.official_email || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Personal Email</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.personal_email || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Phone Number</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.primary_phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Personal Details Group */}
          <div className="flex flex-col gap-4 pt-4 border-t border-slate-100">
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">Personal Details</p>
            
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Gender</p>
                <p className="text-xs font-semibold text-slate-900 capitalize text-right">{employee.gender || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Blood Group</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.blood_group || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Marital Status</p>
                <p className="text-xs font-semibold text-slate-900 capitalize text-right">{employee.marital_status || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Nationality</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.nationality || 'N/A'}</p>
              </div>
              <div className="flex items-start justify-between">
                <p className="text-xs text-slate-500 font-medium">Date of Birth</p>
                <p className="text-xs font-semibold text-slate-900 text-right">{employee.date_of_birth || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Languages Group */}
          <div className="flex flex-col gap-3 pt-4 border-t border-slate-100">
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">Languages Known</p>
            <p className="text-xs font-semibold text-slate-800">
              {employee.languages_known && employee.languages_known.length > 0 
                ? employee.languages_known.join(', ') 
                : 'N/A'}
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-slate-100 bg-white flex items-center justify-end">
          <button
            onClick={() => {
              onClose();
              navigate(`/employees/profile/${employee.uuid}`);
            }}
            className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition shadow-sm shadow-blue-500/20 cursor-pointer"
          >
            View Full Profile
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </div>
    </div>
  );
}